import { useMemo } from "react";
import type { Color } from "@deck.gl/core";
import { ENVIRONMENT } from "src/js/util/environment";
import { SOURCE_STATUSES } from "react-migration/domains/constraints/constants";
import {
  ZoomPointScale,
  SimplifyProfile,
  CollisionFilter,
} from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType";
import { useHasPerfSetting } from "react-migration/domains/nav/components/Modals/DebugModal/performanceSettingsStore";
import { BitmapTileLayer } from "react-migration/lib/map/layers/BitmapTileLayer";
import { useMapLayer } from "react-migration/lib/map/useMapLayer";
import { LtIconKey } from "../../designation/style/icons";
import {
  defaultDesignationStyleAccessors,
  DesignationFeatureProps,
  DesignationLayer,
  DesignationLayerLight,
  DesignationStyleAccessors,
} from "./DesignationLayer";
import { getCategoryFillColour } from "../../designation/style/accessors";
import { RasterWithUnderZoom } from "../../../../lib/map/layers/RasterWithUnderzoom/RasterWithUnderzoom";
import { PermitsClusteredViewportSearchMapLayer } from "react-migration/layouts/map/Multilayer/layer_types/PermitsLayerType/PermitsLayerMapLayer/PermitsClusteredViewportSearchMapLayer";
import { darkenDeckGlColour } from "react-migration/lib/map/createDeckGlColorInterpolator";
import { TRANSPARENT } from "../../designation/style/colours";

export interface ConstraintLayerProps {
  showLabels?: boolean;
  rootCategory: string;
  iconKeySet?: LtIconKey[];
  includeSourceStatuses?: boolean;
  mapType?: string;
  /** Minimum zoom level to display constraints (deck.gl value is 1 lower than google maps) */
  minZoom: number;
  maxZoom?: number;
  zOrder?: number;
  layerName?: string;
  layerId: string;
  featureIsVisible?: (featureProperties: DesignationFeatureProps) => boolean;
  featureIsSelected?: (featureProperties: DesignationFeatureProps) => boolean;
  designationAttributes?: string[];
  dedupePoints?: boolean;
  filterAreaByZoom?: boolean;
  zoomPointScale?: ZoomPointScale;
  simplifyProfile?: SimplifyProfile;
  collisionFilter?: CollisionFilter;
  styleAccessors?: DesignationStyleAccessors;
  forcedRasterCategories?: string[];
  displayByAttribute?: string;
  autoHighlight?: boolean;
}

export type ConstraintsTileOptions = Pick<
  ConstraintLayerProps,
  "rootCategory" | "dedupePoints" | "filterAreaByZoom" | "simplifyProfile" | "designationAttributes"
>;

export const CONSTRAINT_LAYER_CLASSES = [
  DesignationLayer,
  DesignationLayerLight,
  RasterWithUnderZoom,
  PermitsClusteredViewportSearchMapLayer,
];

export const buildConstraintsTileUrl = ({
  rootCategory,
  designationAttributes,
  dedupePoints,
  filterAreaByZoom,
  simplifyProfile,
}: ConstraintsTileOptions) => {
  const queryParams = new URLSearchParams();

  if (designationAttributes?.length) queryParams.append("attributes", designationAttributes.join(","));

  queryParams.append("sourceStatuses", SOURCE_STATUSES.map((x) => x.id).join(","));

  if (dedupePoints !== undefined) {
    queryParams.append("dedupePoints", String(dedupePoints));
  }

  if (filterAreaByZoom !== undefined) {
    queryParams.append("filterAreaByZoom", String(filterAreaByZoom));
  }

  if (simplifyProfile !== undefined) {
    queryParams.append("simplifyProfile", simplifyProfile);
  }

  let tileUrl = `${ENVIRONMENT.CONSTRAINTS_VEC_TILE_API_URL}`;
  tileUrl = tileUrl.replace("{root_category}", encodeURIComponent(rootCategory));
  tileUrl = `${tileUrl}?${queryParams}`;

  return tileUrl;
};

const buildConstraintsRasterTileUrl = ({
  rootCategory,
  color,
}: Pick<ConstraintsTileOptions, "rootCategory"> & { color: Color }) => {
  const queryParams = new URLSearchParams();

  queryParams.append("color", JSON.stringify(color));

  let tileUrl = `${ENVIRONMENT.CONSTRAINTS_VEC_TILE_API_URL}`;
  tileUrl = tileUrl.replace("tiles", "tiles/raster");
  tileUrl = tileUrl.replace("mvt", "png");
  tileUrl = tileUrl.replace("{root_category}", encodeURIComponent(rootCategory));
  tileUrl = `${tileUrl}?${queryParams}`;

  return tileUrl;
};

const RASTER_CATEGORIES = [
  "flood_zones_outer",
  "very_low_risk_of_flooding_from_rivers_and_seas",
  "low_risk_of_flooding_from_rivers_and_seas",
  "medium_risk_of_flooding_from_rivers_and_seas",
  "high_risk_of_flooding_from_rivers_and_seas",
  "low_risk_of_flooding_from_surface_water",
  "medium_risk_of_flooding_from_surface_water",
  "high_risk_of_flooding_from_surface_water",
  "rights_of_way",
  "national_landscape",
  "conservation_areas_outer",
  "employment_area_outer",
  "settlement_boundaries_outer",
  "green_belt_outer",
  "nutrient_neutrality",
];

const FEATURE_IS_VISIBLE_DEFAULT = () => true;

export function ConstraintLayer({
  rootCategory,
  showLabels = false,
  iconKeySet: iconKeys,
  minZoom,
  maxZoom,
  zOrder,
  layerName,
  layerId,
  featureIsVisible = FEATURE_IS_VISIBLE_DEFAULT,
  featureIsSelected,
  zoomPointScale,
  designationAttributes,
  dedupePoints,
  filterAreaByZoom,
  simplifyProfile,
  collisionFilter,
  styleAccessors,
  forcedRasterCategories,
  displayByAttribute,
  autoHighlight,
}: ConstraintLayerProps) {
  const hasRasterConstraintsBeta = useHasPerfSetting("rasterConstraints");

  const mvtUrl = useMemo(() => {
    return buildConstraintsTileUrl({
      rootCategory,
      designationAttributes,
      dedupePoints,
      filterAreaByZoom,
      simplifyProfile,
    });
  }, [rootCategory, designationAttributes, dedupePoints, filterAreaByZoom, simplifyProfile]);

  const LayerClass = useMemo(() => {
    if (
      forcedRasterCategories?.includes(rootCategory) ||
      (hasRasterConstraintsBeta && RASTER_CATEGORIES.includes(rootCategory))
    )
      return BitmapTileLayer;
    // The newer DesignationLayerLight does not yet support labels, styling by attribute, or icons (it would seem)
    if (designationAttributes?.length || iconKeys?.length || showLabels) return DesignationLayer;
    return DesignationLayerLight;
  }, [
    designationAttributes?.length,
    iconKeys?.length,
    showLabels,
    rootCategory,
    hasRasterConstraintsBeta,
    forcedRasterCategories,
  ]);

  useMapLayer(() => {
    return {
      zOrder,
      layer:
        LayerClass === BitmapTileLayer
          ? new RasterWithUnderZoom({
              id: layerId,
              url: buildConstraintsRasterTileUrl({
                rootCategory,
                color: getCategoryFillColour(rootCategory),
              }),
              underZoomLevel: 12,
              hideZoomLevel: minZoom,
            })
          : new LayerClass({
              id: layerId,
              data: mvtUrl,
              showLabels,
              collisionFilter: collisionFilter,
              visible: true,
              iconKeySet: iconKeys,
              minZoom,
              maxZoom,
              pickable: true,
              autoHighlight: autoHighlight,
              uniqueIdProperty: "designation_id",
              featureIsVisible,
              featureIsSelected,
              zoomPointScale,
              styleAccessors,
              displayByAttribute,
              highlightColor(pickingInfo) {
                if (!pickingInfo?.object) {
                  return TRANSPARENT as number[];
                }

                const color = (styleAccessors || defaultDesignationStyleAccessors).getDesignationLineColour(
                  pickingInfo.object.properties
                );

                // Idk why TS doesnt like the Color type
                return darkenDeckGlColour(color, 0.2) as number[];
              },
              loadOptions: {
                mvt: { layers: [layerName] },
              },
            }),
    };
  }, [
    zOrder,
    LayerClass,
    maxZoom,
    mvtUrl,
    minZoom,
    layerId,
    rootCategory,
    showLabels,
    collisionFilter,
    iconKeys,
    featureIsVisible,
    featureIsSelected,
    zoomPointScale,
    styleAccessors,
    layerName,
    displayByAttribute,
    autoHighlight,
  ]);
  return null;
}
